import { useState, useCallback, useEffect } from 'react'
import { X, Download } from 'lucide-react'
import { Spinner } from '@neo-agents/ui'

interface ImagePreviewProps {
  src: string
  open: boolean
  onClose: () => void
}

export function ImagePreview({ src, open, onClose }: ImagePreviewProps) {
  const [loaded, setLoaded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Reset loaded state when src changes or dialog reopens
  useEffect(() => {
    if (!open) {
      setLoaded(false)
      return
    }
    // Pre-load via Image object so onLoad fires even from cache
    const img = new Image()
    img.src = src
    if (img.complete) {
      setLoaded(true)
    } else {
      img.onload = () => setLoaded(true)
    }
  }, [open, src])

  // Close on Escape key
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const handleSave = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      const response = await fetch(src, { credentials: 'include', referrerPolicy: 'no-referrer' })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // Fallback: open in new tab
      window.open(src, '_blank', 'noopener,noreferrer')
    } finally {
      setIsSaving(false)
    }
  }, [src, isSaving])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Top-right actions */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <button
          className={`rounded-base border-2 border-white/30 bg-black/50 p-2 transition-colors ${isSaving ? 'text-white/50 cursor-not-allowed' : 'text-white hover:bg-black/70'}`}
          onClick={(e) => {
            e.stopPropagation()
            handleSave()
          }}
          disabled={isSaving}
          title="Save image"
        >
          {isSaving ? <Spinner className="h-5 w-5" /> : <Download className="h-5 w-5" />}
        </button>
        <button
          className="rounded-base border-2 border-white/30 bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Loading spinner — absolutely centered, independent of img */}
      {!loaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <Spinner className="text-white text-2xl" />
        </div>
      )}

      {/* Image — invisible until fully loaded, then fade in */}
      <img
        src={src}
        alt="Preview"
        referrerPolicy="no-referrer"
        className={`relative z-10 max-w-[90vw] max-h-[90vh] object-contain rounded-base transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}
